'use client';

import { Fragment, MouseEvent as ReactMouseEvent, ReactElement, useEffect, useState } from 'react';
import cn from 'classnames';
import { FontIcon, ImageContainer, Lock, MovieDetails, Separator, Skeleton, Text } from '@intigral/shared';
import withResponsiveStyles from '@intigral/shared/components/hocs/ResponiseStyles/withResponsiveStyles';
import { MovieDetailsProps } from '@intigral/shared/components/MovieDetails';
import styles from './program-without-poster.module.sass';
import { Modal } from '../Modal';
import { useMediaQuery } from '@intigral/hooks';

export interface IProgram {
    channelImage: string;
    channelTitle: string;
    text: string;
    duration: string;
    name: string;
    PlayVideoButton?: any;
    /** If true Play button displayed; otherwise Info button displayed */
    showPlay: boolean;
    showProgress: boolean;
    progress: number;
    poster: string;
    rating?: string;
    description?: string;
    /** Badge with text, e.g. `live`, `rewind` */
    badge?: ReactElement;
    addReminderButton?: ReactElement;
    isReminderSet?: boolean;
    isLocked?: boolean;
    onLockClick?: () => void;
    isLoading?: boolean;
    detailsButton?: ReactElement;
}

export interface IProgramWithoutPoster {
    program: IProgram;
    /** Depending on `type` layout is changed, e.g. showing channel logo if type is `live` */
    type?: 'live' | 'rewind';
    /** If `true` live program's progress is shown */
    showProgress?: boolean;
    /** Asset duration, genre, etc. */
    assetDetails?: MovieDetailsProps;
    noHeight?: boolean;
    /**
     * CTAs (e.g. Play/Details buttons) to be shown in a drawer that user can open on mobile screens
     */
    drawerCTAs?: ReactElement[];
    /**
     * Desktop fallback for non-card layouts: three-dot opens this instead of the modal.
     * Card (Continue Watching) always opens the quick-action modal on every breakpoint.
     */
    onMore?: (event: ReactMouseEvent<HTMLElement>) => void;
    /** Called when the mobile quick-action drawer opens (3-dot menu). */
    onDrawerOpen?: () => void;
    /** Called when the mobile quick-action drawer closes (close icon, backdrop, etc.). */
    onDrawerClose?: () => void;
    /**
     * Increment to programmatically close an open mobile drawer (e.g. after a successful
     * Continue Watching delete). Closed drawers ignore the signal.
     */
    drawerCloseSignal?: number;
    /** `card` is Figma "Cards3.0/Continue Watching" */
    variant?: 'panel' | 'card';
}

const SeparatorStyle = withResponsiveStyles(Separator);

const ProgramWithoutPoster = ({
    program,
    type = 'live',
    showProgress,
    noHeight,
    assetDetails,
    drawerCTAs = [],
    onMore,
    onDrawerOpen,
    onDrawerClose,
    drawerCloseSignal = 0,
    variant = 'panel',
}: IProgramWithoutPoster) => {
    const isCard = variant === 'card';
    const isMobile = useMediaQuery('(max-width: 924px)');
    // Card: three-dot + modal on desktop and mobile. Other layouts: mobile drawer, or desktop onMore.
    const showDrawerTrigger = drawerCTAs.length > 0 && (isMobile || Boolean(onMore) || isCard);
    const {
        channelImage,
        channelTitle,
        text,
        duration,
        poster,
        progress,
        addReminderButton,
        isReminderSet,
        isLocked = false,
        onLockClick = undefined,
        isLoading,
    } = program;
    const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);

    useEffect(() => {
        if (!drawerCloseSignal || !isDetailsPopupOpen) {
            return;
        }
        onDrawerClose?.();
        setIsDetailsPopupOpen(false);
        // Only react to the close signal; intentionally omit isDetailsPopupOpen/onDrawerClose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerCloseSignal]);

    if (isLoading) {
        return isCard ? (
            <div className={cn(styles.program, styles.card)}>
                <div className={styles.details}>
                    <div className={styles.name}>
                        <Skeleton height={22} />
                        <Skeleton height={16} width="60%" />
                    </div>
                </div>
            </div>
        ) : (
            <div className={cn(styles.program, styles.skeleton)}>
                <Skeleton absolute />
            </div>
        );
    }

    return (
        <div className={cn(styles.program, noHeight && styles.noheight, isCard && styles.card)}>
            <div className={cn(styles.details, { [styles['with-poster']]: poster })}>
                {showProgress && !isCard && (
                    <div className={styles['progress-bar']}>
                        <div className={styles['progress-bar-done']} style={{ width: `${progress || '0'}%` }} />
                    </div>
                )}
                {channelImage && type === 'live' && (
                    <>
                        <div className={styles['channel-logo']}>
                            <ImageContainer src={channelImage} alt={channelTitle} />
                        </div>
                        <SeparatorStyle orientation="vertical" marginStart="1x" marginEnd="2x" />
                    </>
                )}
                <div className={styles.name}>
                    <Text
                        as="h3"
                        variant="text4"
                        alignment="start"
                        margin="0"
                        color="white"
                        fontWeight="regular"
                        title={text}
                    >
                        {text}
                    </Text>
                    {duration && (
                        <Text as="span" variant="text5" color="gray" className={styles.duration}>
                            {duration}
                        </Text>
                    )}
                    {assetDetails && (
                        <div className={styles['asset-details']}>
                            <MovieDetails {...assetDetails} />
                        </div>
                    )}
                </div>
                {isLocked ? (
                    // TODO: span instead div?
                    <div onClick={onLockClick}>
                        <Lock />
                    </div>
                ) : addReminderButton ? (
                    <span
                        className={cn(styles['reminder-cta'], { [styles.reminded]: isReminderSet })}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        {addReminderButton}
                    </span>
                ) : showDrawerTrigger ? (
                    // TODO: move to a separate component
                    <span
                        className={styles['drawer-trigger']}
                        data-drawer-trigger="true"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isMobile && onMore && !isCard) {
                                onMore(e);
                                return;
                            }
                            onDrawerOpen?.();
                            setIsDetailsPopupOpen(true);
                        }}
                    >
                        <FontIcon name="more-vertical" />
                    </span>
                ) : null}
            </div>

            <Modal
                open={isDetailsPopupOpen}
                onOpenChange={(open) => {
                    const nextOpen = !!open;
                    if (isDetailsPopupOpen && !nextOpen) {
                        onDrawerClose?.();
                    }
                    setIsDetailsPopupOpen(nextOpen);
                }}
                // Card uses shared Modal (centered desktop / sheet mobile). Non-card keeps sheet class.
                {...(isCard
                    ? { widthSet: 'small' as const }
                    : { alignSelf: 'bottom' as const, className: styles['drawer-sheet'] })}
                closePlacement="end"
                closeSize="small"
            >
                <div className={styles.drawer} data-drawer-modal="true">
                    <Text as="h2" variant="h2" alignment="start" className={styles['drawer-title']}>
                        {program.name || program.text}
                    </Text>
                    <div className={styles['drawer-menu']}>
                        {drawerCTAs.map((cta, idx) => (
                            <Fragment key={`drawer-cta-${idx}`}>{cta}</Fragment>
                        ))}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export { ProgramWithoutPoster };
